import { formatAppCurrency } from '@/renderer/lib/currency';
import type { AccountingDashboardPeriodPreset } from '@/renderer/lib/utils';
import {
  AccountingDashboardAccommodationDailyRow,
  AccountingDashboardCashBalanceDailyRow,
  AccountingDashboardFinancialDailyRow,
  AccountingDashboardGuestOriginRow,
  AccountingDashboardRecentJournalRow,
  AccountingDashboardRevenueGrowthDailyRow,
} from '@/renderer/types/accounting';

export type { AccountingDashboardPeriodPreset };
export { buildDashboardDateRange } from '@/renderer/lib/utils';

export const ACCOUNTING_DASHBOARD_PERIOD_OPTIONS: Array<{
  value: AccountingDashboardPeriodPreset;
  label: string;
}> = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'month', label: 'This month' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
];

export const formatCurrencyValue = (value: number) =>
  formatAppCurrency(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const formatCompactCurrency = (value: number) => {
  return formatAppCurrency(value, {
    notation: Math.abs(value) >= 1000 ? 'compact' : 'standard',
    compactDisplay: 'short',
  });
};

export const formatMetricDateLabel = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));

export const formatJournalTimestamp = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

export const buildCashflowChartData = (
  rows: AccountingDashboardFinancialDailyRow[],
) =>
  rows.map((row) => ({
    label: formatMetricDateLabel(row.metric_date),
    income: row.income_amount,
    expense: row.expense_amount + row.cogs_amount,
    pos: row.pos_revenue_amount,
    accommodation: row.accommodation_revenue_amount,
  }));

export const buildSalesGrowthChartData = (
  rows: AccountingDashboardRevenueGrowthDailyRow[],
) =>
  rows.map((row) => ({
    label: formatMetricDateLabel(row.metric_date),
    revenue: row.total_revenue_amount,
    pos: row.pos_collected_amount,
    accommodation: row.accommodation_revenue_amount,
  }));

export const buildRevenueVsOccupancyChartData = (
  revenueRows: AccountingDashboardRevenueGrowthDailyRow[],
  accommodationRows: AccountingDashboardAccommodationDailyRow[],
) => {
  const occupancyByDate = new Map(
    accommodationRows.map((row) => [row.metric_date, row.occupancy_pct]),
  );

  return revenueRows.map((row) => ({
    label: formatMetricDateLabel(row.metric_date),
    revenue: row.total_revenue_amount,
    occupancy: occupancyByDate.get(row.metric_date) || 0,
  }));
};

export const buildCashBalanceChartData = (
  rows: AccountingDashboardCashBalanceDailyRow[],
) =>
  rows.map((row) => ({
    label: formatMetricDateLabel(row.metric_date),
    opening: row.opening_cash_balance,
    closing: row.closing_cash_balance,
  }));

export const buildRevenueMixData = (
  income: number,
  expenses: number,
  balance: number,
) =>
  [
    { name: 'Income', value: income, color: '#16a34a' },
    { name: 'Expenses', value: expenses, color: '#f97316' },
    { name: 'Balance', value: Math.max(balance, 0), color: '#2563eb' },
  ].filter((item) => item.value > 0);

export const buildGuestOriginPieData = (
  rows: AccountingDashboardGuestOriginRow[],
) => {
  const totalRevenue = rows.reduce((sum, row) => sum + row.total_revenue, 0);

  return rows.map((row) => ({
    name: row.country_name || row.country_iso_code || 'Unknown',
    value: row.total_revenue,
    pct:
      totalRevenue > 0
        ? Number(((row.total_revenue / totalRevenue) * 100).toFixed(1))
        : 0,
  }));
};

export const buildActivitySummaries = (
  entries: AccountingDashboardRecentJournalRow[],
) =>
  Array.from(
    entries
      .reduce<
        Map<
          string,
          {
            sourceLabel: string;
            entryCount: number;
            totalAmount: number;
            latestTimestamp: string;
          }
        >
      >((map, entry) => {
        const key = entry.source_module;
        const sourceLabel =
          key === 'sales'
            ? 'POS settlements'
            : key === 'accommodation'
              ? 'Accommodation postings'
              : key === 'booking_com'
                ? 'Booking.com entries'
                : key === 'inventory'
                  ? 'Inventory journals'
                  : key === 'manual'
                    ? 'Manual journals'
                    : 'System journals';

        const current = map.get(key) || {
          sourceLabel,
          entryCount: 0,
          totalAmount: 0,
          latestTimestamp: entry.created_at,
        };

        current.entryCount += 1;
        current.totalAmount += entry.amount;
        if (entry.created_at > current.latestTimestamp) {
          current.latestTimestamp = entry.created_at;
        }

        map.set(key, current);
        return map;
      }, new Map())
      .values(),
  ).sort((left, right) =>
    right.latestTimestamp.localeCompare(left.latestTimestamp),
  );
